import { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, SegmentedControl, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { TbCalendar } from 'react-icons/tb';
import dayjs from '../../../utils/dayjs';
import toast from 'react-hot-toast';
import {
  useCreatePeriod,
  useCreatePeriodAutomatic,
  useUpdatePeriod
} from '../../../services/period.service';
import { REPORT_NAMINGS, REPORT_TYPES, reportTypesList } from '../../../consts/reportTypes';
import {
  CURRENT_REPORTS_SECTION_KEYS,
  CURRENT_REPORTS_SECTION_OPTIONS,
  FILLING_TYPE,
  FILLING_TYPE_ARRAYS,
  FORM_PF_FILLING_TYPE_KEYS,
  FORM_PF_FILLING_TYPE_OPTIONS,
  PERIODIC_FILLING_VERSION_KEYS,
  PERIODIC_FILLING_VERSION_OPTIONS
} from '../components/CreateProject/data';
import { useUserGroupsAllUsers } from '../../../services/user.service';
import { extractApiError, getYearsForLastFiveYears } from '../../../utils';
import { isFromScratch } from '../Quarters/data';
import { NOT_STARTED } from '../ProjectListV2/periodSteps';
import { findNextPeriod } from '../Quarters';
import { DX_ROOT_CLASS } from '../../../theme/v2';
import {
  Body,
  Choice,
  Collected,
  CollectedList,
  Footer,
  FormPFBlock,
  Note,
  Row,
  SectionLabel,
  Split,
  Working
} from '../CreateProjectV2/style';
import { DeadlineCalculator, OptionCards, describedOption } from '../CreateProjectV2/FormPFChoices';
import { CarryRow, Context } from './style';

const PERIODS = ['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'Y1', 'X1', 'X2'];

/* Form PF files by quarter or annually and by nothing else, and it calls them by name. The keys are
   the same ones stored for every other report type. */
const PF_PERIODS = [
  { value: 'Q1', label: 'Quarter 1' },
  { value: 'Q2', label: 'Quarter 2' },
  { value: 'Q3', label: 'Quarter 3' },
  { value: 'Q4', label: 'Quarter 4' },
  { value: 'Y1', label: 'Annual' }
];

/**
 * Creating a period — and changing one — in the drawer a project is created in.
 *
 * Same surface, same two halves, same controls — a period is a smaller version of the same act, and
 * giving it a third look would have been a third thing to keep in step. What differs is the right
 * half: a new project collects the funds it will have, and a new period inherits them, so that half
 * says what comes across instead of asking for anything.
 *
 * That inheritance is the whole point of the step and it used to be invisible: with an earlier
 * period in place the server clones it whole (`periods/automatic` — funds, jurisdictions, file
 * mapping, transformation rules), and the drawer this replaces asked for a report type and a
 * project name without ever mentioning it.
 *
 * The two mutations are the legacy page's own, chosen by its own test: a workflow with a
 * from-scratch period behind it clones; the first period of a project cannot, and is set up by hand.
 *
 * `editing` turns the same surface into the edit form, because what a period IS is the two things
 * this drawer already asks for. The right half stops promising what will be carried over — that
 * happened when the period was created — and states what the period holds instead. Form PF periods
 * do not come here: their quarter is bound up with a filing type, a version and an end date, and
 * changing one without the others is how a period ends up disagreeing with itself; the caller sends
 * those to the legacy drawer, which has all four fields.
 */
export const NewPeriodBlock = ({
  isOpen,
  workflowId,
  workflow,
  periods,
  reportType,
  onDone,
  onCancel,
  /* The period being changed, or null to create. Kept mounted by the caller and driven by `isOpen`,
     which is what makes the drawer slide rather than appear; the two choices below are therefore
     seeded on every open rather than once, or a second period would start on the first one's. */
  editing = null
}) => {
  const previous = periods[0];
  const suggestion = findNextPeriod(previous?.period, previous?.year);

  /* The assignee is stored as an id; the name lives in the same user list the create-project drawer
     already asks for, so this costs a cache hit and nothing more. */
  const { data: allUsers } = useUserGroupsAllUsers();
  const assignedId =
    typeof workflow?.assigned_to === 'string'
      ? workflow.assigned_to
      : workflow?.assigned_to?.azure_object_id;
  const assigneeName =
    workflow?.assigned_to?.name ||
    (allUsers || []).find(user => user.azure_object_id === assignedId)?.name;

  /* The years to choose from, and the one this drawer has chosen.
   *
   * `getYearsForLastFiveYears` counts backwards from today, so it ends at the current year — while
   * the period being suggested is the one AFTER the last period on the project, which on a Q4 is
   * next year. The control then had no button for the year in its own footer: nothing looked
   * selected, and "Create Q1 2027" sat under a row reading 2026 down to 2022. Whatever is chosen is
   * in the list. */
  const years = useMemo(() => {
    const list = getYearsForLastFiveYears().map(String);
    const chosen = String(editing ? editing.year : suggestion.nextPeriodYear);
    return (list.includes(chosen) ? list : [chosen, ...list]).slice(0, 6);
  }, [editing, suggestion.nextPeriodYear]);

  const reportTitle = reportTypesList.find(type => type.key === reportType)?.title || reportType;
  const projectNoun = REPORT_NAMINGS.PROJECT?.[reportType] || 'Project';

  const isEdit = !!editing;
  /* Form PF asks for four more things, and one of them is required: without an end date the period
     cannot be created at all. The redesigned drawer used to ask for a quarter and a year whatever the
     report type, so a Form PF period made here went out with no filing type, no version and no end
     date — reported from the workflow, and the reason those four are here now. Its year is not asked
     for either: Form PF takes it from the end date, as the classic form does. */
  const isFormPF = reportType === REPORT_TYPES.FORM_PF;
  /* Conversion has no transformation stage, so there are no rules to promise or to keep. */
  const isConversion = reportType === REPORT_TYPES.CONVERSION;
  const [period, setPeriod] = useState(isEdit ? editing.period : suggestion.nextPeriod);
  const [year, setYear] = useState(String(isEdit ? editing.year : suggestion.nextPeriodYear));

  /* The server will not open a quarter while the one before it has no report, and the drawer can see
     that coming: no generate task, and the phase field still saying it never started — the one thing
     that field can be trusted for. Said before the press rather than after it. */
  const owed =
    !isEdit && previous && !previous.report_task && previous.report_progress_status === NOT_STARTED
      ? `${previous.period} ${previous.year} needs its report generated before a new period can open.`
      : null;

  /* Why the server would not do it, kept where the refusal happened. A toast is no good here: this
     drawer covers the corner it appears in, so the one message explaining the dead button was drawn
     behind the drawer that caused it. */
  const [refused, setRefused] = useState(null);
  const [filing, setFiling] = useState(FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING);
  const [section, setSection] = useState(CURRENT_REPORTS_SECTION_KEYS.SECTION_5);
  const [version, setVersion] = useState(PERIODIC_FILLING_VERSION_KEYS.LEGACY);
  const [endDate, setEndDate] = useState('');
  const [filingType, setFilingType] = useState(FILLING_TYPE.UPDATING);
  const isPeriodic = filing === FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING;

  useEffect(() => {
    if (!isOpen) return;
    setPeriod(editing ? editing.period : suggestion.nextPeriod);
    setYear(String(editing ? editing.year : suggestion.nextPeriodYear));
    setRefused(null);
    /* `filing_time` holds either the word "Periodic" or the section of a current report, which is
       how the classic form stores it too — so which of the two it is has to be read back out. */
    const time = editing?.filing_time;
    const isCurrent = !!time && time !== FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING;
    setFiling(
      isCurrent
        ? FORM_PF_FILLING_TYPE_KEYS.CURRENT_REPORTS
        : FORM_PF_FILLING_TYPE_KEYS.PERIODIC_FILLING
    );
    setSection(isCurrent ? time : CURRENT_REPORTS_SECTION_KEYS.SECTION_5);
    setVersion(editing?.version ?? PERIODIC_FILLING_VERSION_KEYS.LEGACY);
    setEndDate(editing?.end_date ? dayjs(editing.end_date).format('YYYY-MM-DD') : '');
    setFilingType(editing?.filing_type ?? FILLING_TYPE.UPDATING);
  }, [isOpen, editing, suggestion.nextPeriod, suggestion.nextPeriodYear]);

  const createAutomatic = useCreatePeriodAutomatic();
  const createInitial = useCreatePeriod();
  const updatePeriod = useUpdatePeriod();

  const clones = periods.some(isFromScratch);
  const mutation = isEdit ? updatePeriod : clones ? createAutomatic : createInitial;

  const fundNoun = REPORT_NAMINGS.FUND?.[reportType] || 'AIF';
  const jurisdictionNoun = REPORT_NAMINGS.JURISDICTION?.[reportType] || 'Jurisdictions';

  /* By name, once each: a period carries a row per file per fund, so the same workbook comes back
     three and four times over. */
  const previousFiles = [
    ...new Set((previous?.files || []).map(file => file.name).filter(Boolean))
  ];

  /* Exactly what the classic form builds, so a period created here is indistinguishable from one
     created there — including the year, which Form PF derives from the end date rather than asking. */
  const pfFields = isFormPF
    ? {
        filing_time: isPeriodic ? filing : section,
        version,
        end_date: endDate,
        filing_type: filingType
      }
    : {};
  const yearNumber = isFormPF ? dayjs(endDate).year() : Number(year);
  const incomplete = isFormPF && !endDate;
  /* What the button will say it is making. Blank on a Form PF period with no date yet — there is no
     year to name until the date names it. */
  const shownYear = isFormPF ? (endDate ? yearNumber : '') : year;
  /* Nothing to save: everything this drawer asks for is what it was opened with. */
  const settled =
    isEdit &&
    period === editing.period &&
    String(shownYear) === String(editing.year) &&
    (!isFormPF ||
      (endDate === (editing.end_date ?? '').slice(0, 10) &&
        filingType === editing.filing_type &&
        version === editing.version &&
        (isPeriodic ? filing : section) === editing.filing_time));

  const onCreate = () => {
    setRefused(null);
    if (isEdit) {
      /* A PUT: what is left out is left behind. The two the drawer asks for change, and every other
         field the period carries goes back as it came, so editing a quarter cannot quietly clear
         something this form never showed. */
      const data = { year: yearNumber, period, workflow_id: workflowId, ...pfFields };
      updatePeriod.mutate(
        { periodId: editing.id, data },
        {
          onSuccess: () => {
            toast.success('Reporting period updated', { position: 'bottom-center' });
            onDone();
          },
          onError: err => setRefused(extractApiError(err) || 'The server would not save it.')
        }
      );
      return;
    }
    mutation.mutate(
      { year: yearNumber, period, workflow_id: workflowId, ...pfFields },
      {
        /* The period that was made goes back with the news. The page this drawer was written for
           only refetches its list, and ignores it; a caller that is standing inside another period
           has somewhere to go with it. */
        onSuccess: created => {
          toast.success('New reporting period has been created', { position: 'bottom-center' });
          onDone(created);
        },
        onError: err => setRefused(extractApiError(err) || 'The server would not create it.')
      }
    );
  };

  return (
    <Drawer
      opened={isOpen}
      onClose={onCancel}
      position="right"
      title={isEdit ? 'Edit period' : 'New period'}
      size={860}
      closeOnClickOutside={false}
      styles={{
        title: { fontSize: 16, fontWeight: 600 },
        content: { display: 'flex', flexDirection: 'column' },
        body: { flex: 1, minHeight: 0, padding: 0, display: 'flex', flexDirection: 'column' }
      }}
    >
      {/* The drawer renders in a portal, outside the page's `dx-v2` root, where `--dx-*` resolve to
          nothing — every token-built rule inside would be dropped silently. The scope comes with it. */}
      <Body className={DX_ROOT_CLASS}>
        <Split>
          <Working>
            {/* Not questions: a report type cannot change on an existing project, the name is the
                project you are standing in, and the assignee comes with it. The form this replaces
                asked all three and disabled all three. */}
            <Context>
              <div>
                <span className="k">Report</span>
                <span className="v">{reportTitle}</span>
              </div>
              <div>
                <span className="k">{projectNoun} name</span>
                <span className="v" title={workflow?.name}>
                  {workflow?.name || '—'}
                </span>
              </div>
              {!!assigneeName && (
                <div>
                  <span className="k">Assigned to</span>
                  <span className="v">{assigneeName}</span>
                </div>
              )}
            </Context>

            <Choice>
              <div className="label">Period</div>
              <SegmentedControl
                fullWidth
                data={
                  isFormPF ? PF_PERIODS : PERIODS.map(option => ({ value: option, label: option }))
                }
                value={period}
                onChange={setPeriod}
              />
            </Choice>

            {/* Form PF has no year of its own to pick: it is the year the period ends in, and the
                classic form reads it off this date rather than asking twice. The block is the
                new-project drawer's, so the two ask for Form PF the same way. */}
            {isFormPF ? (
              <FormPFBlock>
                <Choice>
                  <div className="label">Filing</div>
                  <OptionCards
                    options={FORM_PF_FILLING_TYPE_OPTIONS}
                    value={filing}
                    onChange={setFiling}
                  />
                </Choice>

                {/* Which of the two the filing needs: a periodic one is a version of the form, a
                    current report is a section of it. Only ever one of them is a question. */}
                <Choice>
                  <div className="label">{isPeriodic ? 'Version' : 'Section'}</div>
                  <OptionCards
                    options={
                      isPeriodic
                        ? PERIODIC_FILLING_VERSION_OPTIONS
                        : CURRENT_REPORTS_SECTION_OPTIONS
                    }
                    value={isPeriodic ? version : section}
                    onChange={isPeriodic ? setVersion : setSection}
                  />
                </Choice>

                <Row>
                  {isPeriodic && (
                    <Select
                      label="Filing type"
                      className="grow"
                      data={FILLING_TYPE_ARRAYS}
                      value={filingType}
                      onChange={value => value && setFilingType(value)}
                      allowDeselect={false}
                      renderOption={describedOption}
                      comboboxProps={{ withinPortal: true, width: 320, position: 'bottom-start' }}
                    />
                  )}
                  <DatePickerInput
                    label="End of this reporting period"
                    placeholder="Select date"
                    required
                    className="grow"
                    valueFormat="D MMM YYYY"
                    rightSection={<TbCalendar size={16} />}
                    value={endDate ? dayjs(endDate).toDate() : null}
                    onChange={date => setEndDate(date ? dayjs(date).format('YYYY-MM-DD') : '')}
                    description="Fund-specific end dates are set later, on the report page."
                    // Under the field, so it lines up with the filing type beside it.
                    inputWrapperOrder={['label', 'input', 'description', 'error']}
                  />
                </Row>

                {isPeriodic && version === PERIODIC_FILLING_VERSION_KEYS.LEGACY && (
                  <DeadlineCalculator />
                )}
              </FormPFBlock>
            ) : (
              <Choice>
                <div className="label">Year</div>
                <SegmentedControl fullWidth data={years} value={year} onChange={setYear} />
              </Choice>
            )}

            <Note>
              {isEdit
                ? isConversion
                  ? 'Only what this period is called. Everything in it — funds and files — stays where it is.'
                  : 'Only what this period is called. Everything in it — funds, files, mapping, rules — stays where it is.'
                : previous
                  ? `Follows ${previous.period} ${previous.year}, the last period on this project.`
                  : 'The first period of this project.'}
            </Note>
          </Working>

          {/* File names run to sixty characters, and without this the half they are in refuses to
              shrink to its stated 372px — it takes the width of the longest name and squeezes the
              controls in the other half off the edge. */}
          <Collected style={{ minWidth: 0 }}>
            <SectionLabel style={{ padding: '0 18px' }}>
              {isEdit
                ? `In ${editing.period} ${editing.year}`
                : clones
                  ? `Carried over from ${previous.period} ${previous.year}`
                  : 'Set up by hand'}
            </SectionLabel>

            <CollectedList style={{ minWidth: 0 }}>
              {isEdit ? (
                /* What is in there already. The half that promises an inheritance has nothing to
                   promise on a period that exists — but "what am I about to rename" is a fair
                   question, and these are the same three rows that would have answered it. */
                <>
                  <CarryRow>
                    <span>{fundNoun}s</span>
                    <b>{editing.fund_count ?? 0}</b>
                  </CarryRow>
                  <CarryRow>
                    <span>{jurisdictionNoun}</span>
                    <b>{editing.jurisdiction_count ?? 0}</b>
                  </CarryRow>
                  <CarryRow>
                    <span>Source files</span>
                    <b>{editing.file_count ?? 0} files</b>
                  </CarryRow>
                </>
              ) : clones ? (
                <>
                  <CarryRow>
                    <span>{fundNoun}s</span>
                    <b>{previous.fund_count ?? 0}</b>
                  </CarryRow>
                  <CarryRow>
                    <span>{jurisdictionNoun}</span>
                    <b>{previous.jurisdiction_count ?? 0}</b>
                  </CarryRow>
                  {!isConversion && (
                    <>
                      <CarryRow>
                        <span>
                          File mapping <em>— which sheet feeds which field</em>
                        </span>
                        <b>{previous.file_count ?? 0} files</b>
                      </CarryRow>
                      <CarryRow>
                        <span>Transformation rules</span>
                        <b>copied</b>
                      </CarryRow>
                    </>
                  )}

                  {!!previousFiles.length && (
                    <>
                      <SectionLabel style={{ padding: '26px 18px 8px' }}>
                        Files you will need this quarter
                      </SectionLabel>
                      {previousFiles.slice(0, 6).map(name => (
                        <CarryRow key={name} data-plain="true">
                          <span title={name}>{name}</span>
                        </CarryRow>
                      ))}
                      {previousFiles.length > 6 && (
                        <CarryRow data-plain="true">
                          <span>
                            + {previousFiles.length - 6} more from {previous.period} {previous.year}
                          </span>
                        </CarryRow>
                      )}
                    </>
                  )}
                </>
              ) : (
                <span className="empty">
                  {isConversion
                    ? 'Nothing to carry over — this is the first period of the project, so the funds are set up by hand.'
                    : 'Nothing to carry over — this is the first period of the project, so the funds and the sheet mapping are set up by hand.'}
                </span>
              )}
            </CollectedList>

            <Note style={{ padding: '0 18px' }}>
              {isEdit
                ? 'None of it moves — the period keeps everything it has and answers to a different name.'
                : clones
                  ? `All of it comes across the moment the period is created. This quarter’s files are uploaded after that, and ${isConversion ? 'the report is built from them' : 'extraction runs on them'}.`
                  : isConversion
                    ? 'Files come first, then the report is built from them.'
                    : 'Files come first, then the sheets are mapped in Extraction.'}
            </Note>
          </Collected>
        </Split>

        <Footer>
          <span className={refused || owed ? 'recap refused' : 'recap'}>
            {refused || owed
              ? refused || owed
              : incomplete
                ? 'An end date is required — Form PF takes the year from it.'
                : isEdit
                  ? `${editing.period} ${editing.year} → ${period} ${shownYear}`
                  : clones
                    ? isConversion
                      ? `${previous.fund_count ?? 0} ${fundNoun}s and ${previous.jurisdiction_count ?? 0} ${jurisdictionNoun.toLowerCase()}${previous.jurisdiction_count === 1 ? '' : 's'} come across`
                      : `${previous.fund_count ?? 0} ${fundNoun}s and the mapping of ${previous.file_count ?? 0} files come across`
                    : 'Set up by hand after it exists'}
          </span>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="default" onClick={onCancel} disabled={mutation.isLoading}>
              Cancel
            </Button>
            <Button
              onClick={onCreate}
              loading={mutation.isLoading}
              disabled={incomplete || settled || !!owed}
            >
              {isEdit ? 'Save' : 'Create'} {period} {shownYear}
            </Button>
          </div>
        </Footer>
      </Body>
    </Drawer>
  );
};
